import { type JSX, useState } from "react";

import { type Environment, type SessionView, selectedRoles } from "../api/bench";
import type { CatalogController } from "../hooks/useCatalog";
import { ActivityList } from "./ActivityList";
import { AttachRepositoryPanel } from "./AttachRepositoryPanel";
import { OperationNav } from "./OperationNav";
import { OperationPanel } from "./OperationPanel";
import { ResultPanel } from "./ResultPanel";
import { StatsDashboard } from "./StatsDashboard";
import { UsagePanel } from "./UsagePanel";
import { VerifyPanel } from "./VerifyPanel";

/** What the three top-level tabs are for. Owned by the page, which draws the bar. */
export type MainTab = "overview" | "api" | "setup";

export const MAIN_TABS: { key: MainTab; label: string; hint: string }[] = [
  { key: "overview", label: "Overview", hint: "What the platform has been used for" },
  { key: "api", label: "API calls", hint: "Every endpoint the deployment exposes" },
  { key: "setup", label: "Set up", hint: "Create a repository and attach it" },
];

interface OperationsScreenProps {
  environment: Environment;
  session: SessionView;
  catalog: CatalogController;
  tab: MainTab;
}

/**
 * The three things a tester comes here to do, kept apart.
 *
 * They were one list before: reporting views and a repository-provisioning screen sat in the
 * same navigation as the fifty-five API operations, which made the two that are not API calls
 * look like two more of them. Reading what happened, exercising an endpoint and preparing a
 * fixture are separate jobs, so they are separate tabs -- and Overview comes first because it is
 * the one you want before deciding what to poke.
 */
export function OperationsScreen({
  environment,
  session,
  catalog,
  tab,
}: OperationsScreenProps): JSX.Element {
  // Which project the dashboard sent us to look at, and which Overview view is showing. Held
  // here so the hop from "Inspect" survives the switch between them.
  const [inspecting, setInspecting] = useState<string | null>(null);
  const [overview, setOverview] = useState<"dashboard" | "activity" | "tokens">("dashboard");
  const { state } = catalog;

  if (state.kind === "idle" || state.kind === "loading") {
    return <section className="panel">Reading the API schema from {environment.label}…</section>;
  }

  if (state.kind === "error") {
    return (
      <section className="panel">
        <div className="notice notice--error">
          <h3>The API schema could not be read</h3>
          <p>{state.message}</p>
          <p className="muted">
            Without it the bench cannot build its forms. Check that {environment.label} is
            reachable, then try again.
          </p>
        </div>
        <div className="panel__actions">
          <button type="button" className="primary" onClick={() => void catalog.reload(true)}>
            Try again
          </button>
        </div>
      </section>
    );
  }

  const operation = state.catalog.operations.find(
    (candidate) => candidate.operation_id === catalog.selected,
  );

  return (
    <>
      {tab === "overview" && (
        <>
          <nav className="tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={overview === "dashboard"}
              className={
                overview === "dashboard" ? "tabs__tab tabs__tab--active" : "tabs__tab"
              }
              onClick={() => setOverview("dashboard")}
            >
              Usage dashboard
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={overview === "activity"}
              className={overview === "activity" ? "tabs__tab tabs__tab--active" : "tabs__tab"}
              onClick={() => setOverview("activity")}
            >
              Activity
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={overview === "tokens"}
              className={overview === "tokens" ? "tabs__tab tabs__tab--active" : "tabs__tab"}
              onClick={() => setOverview("tokens")}
            >
              Token usage
            </button>
          </nav>

          {overview === "tokens" ? (
            <UsagePanel />
          ) : overview === "dashboard" ? (
            <StatsDashboard
              environment={environment}
              onInspect={(projectId) => {
                setInspecting(projectId);
                setOverview("activity");
              }}
            />
          ) : (
            <ActivityList environment={environment} initialProject={inspecting} />
          )}
        </>
      )}

      {tab === "setup" && <AttachRepositoryPanel environment={environment} />}

      {tab === "api" && (
        <div className="operations">
          <OperationNav
            catalog={state.catalog}
            selected={catalog.selected}
            onSelect={catalog.select}
            roles={selectedRoles(session)}
          />

          <div className="operations__main">
            {state.stale && (
              <div className="notice notice--warning">
                <strong>Showing a cached API schema.</strong> The deployment could not be
                re-read, so a very recent endpoint or field may be missing.
              </div>
            )}

            {state.catalog.warnings.length > 0 && (
              <div className="notice notice--warning">
                <strong>Some operations could not be read from the schema.</strong>
                <ul>
                  {state.catalog.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {catalog.runError && (
              <div className="notice notice--error">
                <strong>The bench refused to run that.</strong> {catalog.runError}
              </div>
            )}

            {operation ? (
              <OperationPanel
                operation={operation}
                prefill={catalog.prefill}
                profile={session.profile}
                roles={selectedRoles(session)}
                destructiveAllowed={environment.destructive_allowed}
                canDiscoverSecrets={environment.can_discover_webhook_secrets}
                projectSet={Boolean(session.scope.project_id)}
                busy={catalog.running}
                onRun={(input) => void catalog.run(input)}
              />
            ) : (
              <section className="panel">
                <h2>{state.catalog.api_title}</h2>
                <p className="muted">
                  {state.catalog.operations.length} operations from{" "}
                  <code>{environment.ams_base_url}</code>, schema version{" "}
                  <code>{state.catalog.api_version}</code>. Pick a task on the left.
                </p>
                <div className="panel__actions">
                  <button type="button" onClick={() => void catalog.reload(true)}>
                    Re-read the schema
                  </button>
                </div>
              </section>
            )}

            {catalog.result && <ResultPanel result={catalog.result} />}

            {catalog.result && operation && (
              <VerifyPanel
                operation={operation}
                result={catalog.result}
                sent={catalog.lastInput}
                onFollowUp={(operationId, prefill) =>
                  catalog.selectWithPrefill(operationId, prefill)
                }
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
